import { useEffect, useMemo } from "react";
import { Alert, Checkbox, Form, Input, Select, Upload } from "antd";

function makeIdScene() {
  return `REVI-${Date.now()}`;
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

export function StepIdentity({
  fileList,
  setFileList,
  requiredBanner = true,
  // Editando una sala del catálogo Educa360 que REVI comparte: el título y la
  // URL se aplican en las dos verticales (es la misma sala) y la imagen no se
  // toca desde aquí. Hay que decirlo antes de que el admin escriba, no después.
  compartidaEduca360 = false,
}) {
  const form = Form.useFormInstance();
  const initialId = useMemo(() => makeIdScene(), []);

  // Una sala neutra no puede además declarar idiomas: si tiene locución en
  // español, ha dejado de ser neutra. El backend rechaza esa combinación, así
  // que aquí se desactiva el selector en vez de dejar montar un envío que va a
  // fallar.
  const neutral = Form.useWatch("sceneLanguageNeutral", form);

  useEffect(() => {
    if (neutral) form.setFieldsValue({ sceneLanguages: [] });
  }, [neutral, form]);

  useEffect(() => {
    const current = form.getFieldValue("idScene");
    if (!current) form.setFieldsValue({ idScene: initialId });
  }, [form, initialId]);

  const beforeUpload = (file) => {
    const ok =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";

    if (!ok) return Upload.LIST_IGNORE;

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) return Upload.LIST_IGNORE;

    return false; // no auto upload
  };

  const handleChange = ({ fileList: next }) => {
    const normalized = (next || []).slice(-1);
    setFileList(normalized);

    const hasBanner = normalized.length > 0;

    // fake field para validación (si ya hay url, es banner existente)
    const val = hasBanner
      ? normalized[0].url
        ? "existing"
        : "local"
      : undefined;
    form.setFieldsValue({ bannerId: val });

    // ✅ Forzar thumbnail cuando es fichero local (a veces antd no lo genera)
    const f = normalized[0];
    if (f?.originFileObj && !f.thumbUrl) {
      toBase64(f.originFileObj)
        .then((dataUrl) => {
          setFileList((prev) =>
            (prev || []).map((it) =>
              it.uid === f.uid ? { ...it, thumbUrl: dataUrl } : it,
            ),
          );
        })
        .catch(() => {});
    }
  };

  return (
    <>
      <Form.Item name="idScene" style={{ display: "none" }}>
        <Input />
      </Form.Item>

      {compartidaEduca360 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Sala del catálogo Educa360 compartida con REVI"
          description="El título y la URL que cambies aquí se aplican también en Educa360: es la misma sala. La imagen y la descripción de Educa360 no se editan desde este panel."
        />
      ) : null}

      <Form.Item
        name="title"
        label="Título de la sala"
        rules={[{ required: true, message: "El título es obligatorio" }]}
      >
        <Input size="large" placeholder="Ej: Sala REVI - Cognición" />
      </Form.Item>

      <Form.Item
        name="hubsUrl"
        label="URL de la sala (Hubs)"
        rules={[
          { required: true, message: "La URL es obligatoria" },
          { type: "url", message: "Debe ser una URL válida (https://...)" },
        ]}
      >
        <Input size="large" placeholder="https://..." />
      </Form.Item>

      {/*
        Idioma del CONTENIDO de la sala (locución, carteles, voz), no de la
        interfaz. Filtrará el catálogo que ve cada usuario.

        Se puede dejar vacío: "sin clasificar" es un estado válido y es donde
        están hoy todas las salas. Un usuario las sigue viendo, en cualquier
        idioma, hasta que se haga la migración.

        "Sin voz ni texto" NO es lo mismo que dejarlo vacío: marca la sala como
        válida para siempre en todos los idiomas (un paisaje, un vídeo 360 de
        relajación). Es la diferencia que permitirá, el día que se apague la red
        de seguridad, que estas salas sigan apareciendo y las sin clasificar no.
      */}
      <Form.Item
        name="sceneLanguages"
        label="Idioma del contenido"
        help="Déjalo vacío si aún no lo sabes. Marca «sin voz ni texto» solo si la sala vale para cualquier idioma."
      >
        <Select
          size="large"
          mode="multiple"
          allowClear
          placeholder="Sin clasificar"
          disabled={neutral}
          options={[
            { value: "es", label: "Español" },
            { value: "fr", label: "Francés" },
            { value: "en", label: "Inglés" },
          ]}
        />
      </Form.Item>

      <Form.Item name="sceneLanguageNeutral" valuePropName="checked">
        <Checkbox>Sin voz ni texto: vale para cualquier idioma</Checkbox>
      </Form.Item>

      <Form.Item
        name="bannerId"
        label="Banner (imagen)"
        rules={
          requiredBanner
            ? [{ required: true, message: "Debes seleccionar una imagen" }]
            : []
        }
        help={
          compartidaEduca360
            ? "La imagen de una sala compartida se gestiona desde el catálogo Educa360."
            : "Formatos: JPG, PNG, WEBP. Máx 5MB."
        }
      >
        <Upload
          listType="picture-card"
          fileList={fileList}
          maxCount={1}
          beforeUpload={beforeUpload}
          onChange={handleChange}
          accept="image/png,image/jpeg,image/webp"
          disabled={compartidaEduca360}
          showUploadList={{ showRemoveIcon: !compartidaEduca360 }}
        >
          {!compartidaEduca360 &&
            (!fileList || fileList.length < 1) &&
            "+ Subir"}
        </Upload>
      </Form.Item>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12 }}
        message="El banner se sube al servidor al finalizar el asistente y se guarda como FileVirtualScene (bannertop)."
      />
    </>
  );
}
